import React, { useState } from "react";
import { supabase } from "../supabaseClient";

const C = {
  evergreen: "#163C2F",
  forest:    "#214D3A",
  sage:      "#7FB98E",
  ivory:     "#F5F1EA",
  brass:     "#B08D57",
  charcoal:  "#2B2B2B",
};

const TIERS = [
  {
    id:       "free",
    name:     "Free",
    price:    "$0",
    period:   "/mo",
    sub:      "Get started, no card needed",
    features: [
      "Unlimited properties",
      "Unlimited tenants",
      "Basic maintenance tracking",
      "Payment recording",
      "Email support",
    ],
    cta:       "Get Started Free",
    ctaAction: "signup",
    highlight: false,
  },
  {
    id:       "core",
    name:     "Core",
    price:    "$12",
    period:   "/mo",
    sub:      "Everything you need to run your portfolio",
    features: [
      "Everything in Free",
      "Full maintenance tracking",
      "Email notifications & receipts",
      "Payment history & ledger",
      "Document center",
      "Priority email support",
    ],
    cta:       "Start Free Trial",
    ctaAction: "checkout",
    priceId:   "price_1TejaSHgweDWY7O1qtRcvXkz",
    trial:     true,
    highlight: false,
  },
  {
    id:       "pro",
    name:     "Pro",
    price:    "$29",
    period:   "/mo",
    sub:      "For serious landlords",
    features: [
      "Everything in Core",
      "Advanced financial reports",
      "Team member access",
      "Priority support",
    ],
    cta:       "Start Free Trial",
    ctaAction: "checkout",
    priceId:   "price_1TejdMHgweDWY7O1JgmhbIjT",
    trial:     true,
    highlight: true,
    badge:     "Most Popular",
  },
];

const CHECK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

export default function Pricing() {
  const [checkoutLoading, setCheckoutLoading] = useState(null); // priceId being loaded
  const canceled = new URLSearchParams(window.location.search).has("canceled");

  // Scroll to #team section when navigating from the Team Members upgrade button
  React.useEffect(() => {
    if (window.location.hash === "#team") {
      const el = document.getElementById("team");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleCta = async (action, priceId) => {
    if (action === "signup") {
      window.location.href = "/app?signin";
      return;
    }
    if (action !== "checkout" || !priceId) return;

    // Verify the user is signed in before starting checkout
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      window.location.href = "/app?signin";
      return;
    }

    setCheckoutLoading(priceId);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { priceId },
      });
      if (error || !data?.url) {
        const msg = data?.error || error?.message || "Something went wrong.";
        alert(msg);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      alert("Could not start checkout. Please try again.");
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", background: C.ivory, minHeight: "100vh", color: C.charcoal }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Cormorant+Garamond:wght@400;600;700&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <style>{`
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        body{-webkit-font-smoothing:antialiased;}
        .pr-nav{background:${C.evergreen};height:68px;display:flex;align-items:center;justify-content:space-between;padding:0 64px;position:sticky;top:0;z-index:20;border-bottom:1px solid rgba(255,255,255,0.06);}
        .pr-nav-logo{display:flex;align-items:center;gap:10px;text-decoration:none;cursor:pointer;}
        .pr-nav-logo-name{font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:700;color:#F5F1EA;letter-spacing:0.02em;}
        .pr-nav-logo-sub{font-size:9px;color:${C.sage};letter-spacing:0.12em;text-transform:uppercase;margin-top:2px;}
        .pr-nav-links{display:flex;align-items:center;gap:28px;}
        .pr-nav-link{color:rgba(245,241,234,0.65);font-size:13px;font-weight:500;text-decoration:none;transition:color 0.15s;font-family:'Manrope',sans-serif;}
        .pr-nav-link:hover{color:#F5F1EA;}
        .pr-nav-link.active{color:#F5F1EA;font-weight:700;}
        .pr-nav-signin{background:transparent;color:#F5F1EA;border:1.5px solid ${C.brass};border-radius:50px;padding:8px 22px;font-family:'Manrope',sans-serif;font-size:12px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;transition:all 0.15s;}
        .pr-nav-signin:hover{background:${C.brass};color:${C.evergreen};}
        .pr-hero{text-align:center;padding:80px 40px 60px;background:${C.ivory};}
        .pr-eyebrow{display:inline-flex;align-items:center;gap:8px;border:1px solid ${C.brass}44;background:${C.brass}0d;border-radius:50px;padding:6px 18px;font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:${C.brass};margin-bottom:28px;font-family:'Manrope',sans-serif;font-weight:600;}
        .pr-hero-h1{font-family:'DM Serif Display',serif;font-size:clamp(40px,6vw,68px);font-weight:400;color:${C.evergreen};line-height:1.08;margin-bottom:16px;}
        .pr-hero-sub{font-size:17px;color:#5a7060;max-width:520px;margin:0 auto;line-height:1.75;}
        .pr-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;max-width:1060px;margin:0 auto;padding:0 40px 80px;}
        .pr-card{background:#fff;border:1px solid #E8E4DC;border-radius:16px;padding:32px 28px;display:flex;flex-direction:column;position:relative;transition:box-shadow 0.18s;}
        .pr-card:hover{box-shadow:0 6px 24px rgba(22,60,47,0.1);}
        .pr-card.highlight{background:${C.evergreen};border-color:${C.evergreen};color:#F5F1EA;}
        .pr-badge{display:inline-block;background:${C.brass};color:${C.evergreen};font-family:'Manrope',sans-serif;font-size:10px;font-weight:800;letter-spacing:0.1em;text-transform:uppercase;padding:4px 12px;border-radius:50px;margin-bottom:20px;}
        .pr-tier{font-family:'Manrope',sans-serif;font-size:12px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:${C.sage};margin-bottom:10px;}
        .pr-card.highlight .pr-tier{color:${C.sage};}
        .pr-price{font-family:'DM Serif Display',serif;font-size:52px;font-weight:400;color:${C.evergreen};line-height:1;}
        .pr-card.highlight .pr-price{color:#F5F1EA;}
        .pr-period{font-size:14px;font-weight:500;color:#9B9690;margin-left:2px;vertical-align:middle;}
        .pr-card.highlight .pr-period{color:rgba(245,241,234,0.55);}
        .pr-sub{font-size:13px;color:#9B9690;margin-top:8px;line-height:1.5;}
        .pr-card.highlight .pr-sub{color:rgba(245,241,234,0.55);}
        .pr-divider{height:1px;background:#F0EDE6;margin:24px 0;}
        .pr-card.highlight .pr-divider{background:rgba(255,255,255,0.15);}
        .pr-features{display:flex;flex-direction:column;gap:11px;flex:1;margin-bottom:28px;}
        .pr-feature{display:flex;align-items:flex-start;gap:10px;font-size:13px;color:${C.charcoal};}
        .pr-card.highlight .pr-feature{color:rgba(245,241,234,0.85);}
        .pr-check{color:${C.sage};flex-shrink:0;margin-top:1px;}
        .pr-card.highlight .pr-check{color:${C.sage};}
        .pr-cta{width:100%;padding:13px 0;border-radius:999px;font-family:'Manrope',sans-serif;font-size:13px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;cursor:pointer;transition:all 0.15s;border:none;}
        .pr-cta-free{background:${C.ivory};color:${C.evergreen};border:1.5px solid ${C.evergreen};}
        .pr-cta-free:hover{background:${C.evergreen};color:#F5F1EA;}
        .pr-cta-core{background:${C.evergreen};color:#F5F1EA;}
        .pr-cta-core:hover{background:${C.forest};}
        .pr-cta-pro{background:${C.brass};color:${C.evergreen};}
        .pr-cta-pro:hover{background:#c9a568;}
        .pr-faq{max-width:720px;margin:0 auto;padding:0 40px 80px;}
        .pr-faq-h2{font-family:'DM Serif Display',serif;font-size:clamp(28px,4vw,40px);font-weight:400;color:${C.evergreen};text-align:center;margin-bottom:40px;}
        .pr-faq-item{border-bottom:1px solid #E8E4DC;padding:20px 0;}
        .pr-faq-q{font-weight:700;font-size:15px;color:${C.charcoal};margin-bottom:8px;}
        .pr-faq-a{font-size:14px;color:#6b7c6b;line-height:1.7;}
        .pr-footer{background:${C.evergreen};border-top:1px solid rgba(255,255,255,0.08);padding:32px 64px;display:flex;justify-content:space-between;align-items:center;}
        .pr-footer-logo{font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:700;color:#F5F1EA;}
        .pr-footer-sub{font-size:11px;color:rgba(245,241,234,0.35);margin-top:3px;}
        .pr-footer-copy{font-size:12px;color:rgba(245,241,234,0.3);}
        @media(max-width:900px){
          .pr-nav{padding:0 20px;}
          .pr-nav-links a{display:none;}
          .pr-cards{grid-template-columns:1fr;padding:0 20px 60px;}
          .pr-faq{padding:0 20px 60px;}
          .pr-footer{padding:24px 20px;flex-direction:column;gap:12px;text-align:center;}
        }
      `}</style>

      {/* ── Nav ── */}
      <nav className="pr-nav">
        <a href="/" className="pr-nav-logo">
          <img src="/Leasly-logofinal.png" alt="Leasly" width="30" height="30" style={{ borderRadius: 6, objectFit: "contain" }}/>
          <div>
            <div className="pr-nav-logo-name">Leasly</div>
            <div className="pr-nav-logo-sub">by Gibbs Prodigy Group</div>
          </div>
        </a>
        <div className="pr-nav-links">
          <a href="/features"   className="pr-nav-link">Features</a>
          <a href="/pricing"    className="pr-nav-link active">Pricing</a>
          <a href="/#contact"   className="pr-nav-link">Share an Idea</a>
          <button className="pr-nav-signin" onClick={() => window.location.href = "/app?signin"}>Sign In</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="pr-hero">
        <div className="pr-eyebrow">Simple, transparent pricing</div>
        <h1 className="pr-hero-h1">
          Plans that grow<br/>
          <span style={{ fontStyle: "italic", color: C.sage }}>with your portfolio.</span>
        </h1>
        <p className="pr-hero-sub">
          Start free and upgrade as your portfolio grows. Paid plans include a <strong>14-day free trial</strong> — no card charges until the trial ends.
        </p>
        {canceled && (
          <div style={{ marginTop: 20, display: "inline-block", background: "#FEF3C7", border: "1px solid #F59E0B55", borderRadius: 10, padding: "10px 20px", fontSize: 13, color: "#92400E" }}>
            Your checkout was canceled — no charge was made. Choose a plan below when you're ready.
          </div>
        )}
      </section>

      {/* ── Cards ── */}
      <div className="pr-cards">
        {TIERS.map(tier => {
          const isLoading = checkoutLoading === tier.priceId;
          return (
            <div key={tier.id} className={`pr-card${tier.highlight ? " highlight" : ""}`}>
              {tier.badge && <div className="pr-badge">{tier.badge}</div>}
              <div className="pr-tier">{tier.name}</div>
              <div>
                <span className="pr-price">{tier.price}</span>
                <span className="pr-period">{tier.period}</span>
              </div>
              <p className="pr-sub">{tier.sub}</p>
              {tier.trial && (
                <div style={{ display: "inline-block", marginTop: 10, background: tier.highlight ? "rgba(127,185,142,0.2)" : "#EAF2ED", color: tier.highlight ? C.sage : C.evergreen, border: `1px solid ${tier.highlight ? "rgba(127,185,142,0.4)" : "#C5DBC9"}`, borderRadius: 50, padding: "3px 12px", fontSize: 11, fontWeight: 700, fontFamily: "'Manrope',sans-serif", letterSpacing: "0.06em" }}>
                  14-day free trial
                </div>
              )}
              <div className="pr-divider"/>
              <div className="pr-features">
                {tier.features.map((f, i) => (
                  <div key={i} className="pr-feature">
                    <span className="pr-check">{CHECK}</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <button
                className={`pr-cta pr-cta-${tier.id}`}
                disabled={isLoading}
                onClick={() => handleCta(tier.ctaAction, tier.priceId)}
                style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? "not-allowed" : "pointer" }}
              >
                {isLoading ? "Redirecting…" : tier.cta}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Team Add-ons ── */}
      <section id="team" style={{ background: "#F5F1EA", padding: "80px 64px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: 10, color: C.brass, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>Add-ons</div>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(30px,4vw,46px)", fontWeight: 400, color: C.evergreen, margin: "0 0 12px" }}>
              Collaborate with your team
            </h2>
            <p style={{ fontSize: 15, color: "#5a7060", maxWidth: 480, margin: "0 auto", lineHeight: 1.7 }}>
              Add team members to your account with granular permissions. Available on <strong>Core and Pro plans</strong>.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
            {[
              { name: "Team Starter",   price: "$6",  period: "/mo", limit: "Up to 2 team members",   priceId: "price_1TeqJiHgweDWY7O1eNJF0oDV" },
              { name: "Team Plus",      price: "$12", period: "/mo", limit: "Up to 5 team members",   priceId: "price_1TeqK4HgweDWY7O1FWNitDcw" },
              { name: "Team Unlimited", price: "$20", period: "/mo", limit: "Unlimited team members",  priceId: "price_1TeqKVHgweDWY7O1iR8MckuA" },
            ].map(plan => (
              <div key={plan.name} style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 14, padding: "28px 24px", display: "flex", flexDirection: "column" }}>
                <div style={{ fontSize: 10, color: "#9B9690", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, marginBottom: 10 }}>Add-on</div>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, fontWeight: 400, color: C.evergreen, marginBottom: 6 }}>{plan.name}</div>
                <div style={{ marginBottom: 10 }}>
                  <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 38, fontWeight: 400, color: C.charcoal }}>{plan.price}</span>
                  <span style={{ fontSize: 14, color: "#9B9690", marginLeft: 2 }}>{plan.period}</span>
                </div>
                <div style={{ fontSize: 13, color: "#6b7c6b", marginBottom: 22, lineHeight: 1.5 }}>{plan.limit}</div>
                <button
                  style={{ marginTop: "auto", padding: "11px 0", background: C.evergreen, color: C.ivory, border: "none", borderRadius: 999, fontFamily: "'Manrope',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer" }}
                  onClick={() => handleCta("checkout", plan.priceId)}
                >
                  {checkoutLoading === plan.priceId ? "Redirecting…" : "Add to Plan"}
                </button>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 24, textAlign: "center", fontSize: 13, color: "#9B9690" }}>
            Team add-ons require an active Core or Pro subscription. 14-day free trial applies to base plans only.
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="pr-faq">
        <h2 className="pr-faq-h2">Frequently asked questions</h2>
        {[
          {
            q: "Can I switch plans later?",
            a: "Yes — you can upgrade or downgrade at any time. Changes take effect immediately and billing is prorated.",
          },
          {
            q: "What payment methods do you accept?",
            a: "We accept all major credit and debit cards via Stripe. Paid plans are launching soon.",
          },
          {
            q: "Is the Free plan really free forever?",
            a: "Yes. The Free plan never expires and doesn't require a credit card. You get unlimited properties and tenants at no cost.",
          },
          {
            q: "What happens if I exceed my plan's limits?",
            a: "We'll notify you when you're approaching your limit and give you the option to upgrade. We won't lock you out without warning.",
          },
          {
            q: "Does Leasly store tenant payment data?",
            a: "Leasly records payment history and receipts but does not process payments directly. Rent collection (Zelle, cash, etc.) happens outside the platform.",
          },
        ].map(({ q, a }, i) => (
          <div key={i} className="pr-faq-item">
            <div className="pr-faq-q">{q}</div>
            <div className="pr-faq-a">{a}</div>
          </div>
        ))}
      </section>

      {/* ── Footer ── */}
      <footer className="pr-footer">
        <div>
          <div className="pr-footer-logo">Leasly</div>
          <div className="pr-footer-sub">by Gibbs Prodigy Group · Pittsburg, CA</div>
        </div>
        <div className="pr-footer-copy">© 2026 Gibbs Prodigy Group. All rights reserved.</div>
      </footer>
    </div>
  );
}
